import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Paper, Typography, MenuItem, Grid, IconButton, Menu, Box, Tooltip, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ToastContainer } from "react-toastify";
import { ExpandMore as ExpandMoreIcon, Add as AddIcon, Settings as SettingsIcon } from "@material-ui/icons";
import api from "../../services/api";
import KanbanBoard from "../../components/Kanban/KanbanBoard";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%"
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 200
    },
    boardContainer: {
        flexGrow: 1,
        height: "calc(100% - 100px)",
        marginTop: theme.spacing(2)
    },
    titleContainer: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
    },
    titleText: {
        marginRight: theme.spacing(1)
    },
    queueMenuItem: {
        minWidth: 250
    },
    queueName: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        background: theme.palette.action.hover,
        padding: theme.spacing(0.25, 1),
        borderRadius: 8,
        display: "inline-block"
    }
}));

const Kanban = () => {
    const classes = useStyles();
    const titleRef = useRef(null);
    const [queues, setQueues] = useState([]);
    const [selectedQueue, setSelectedQueue] = useState("");
    const [currentQueueName, setCurrentQueueName] = useState("");
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const [titleWidth, setTitleWidth] = useState(undefined);
    const { user } = useContext(AuthContext);

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const { data } = await api.get("/queue");
                
                // Filtrar apenas as filas que o usuário tem acesso
                const userQueues = data.filter(queue => {
                    // Administradores têm acesso a todas as filas
                    if (user.profile === "admin") return true;
                    
                    // Verificar se o usuário tem a fila em suas permissões
                    return user.queues && user.queues.includes(queue.id);
                });
                
                setQueues(userQueues);

                // Se houver filas, selecione a primeira por padrão
                if (userQueues.length > 0) {
                    setSelectedQueue(userQueues[0].id.toString());
                    setCurrentQueueName(userQueues[0].name);
                }

                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };

        fetchQueues();
    }, [user]);

    const handleMenuClose = () => setIsMenuOpen(false);

    const handleTitleClick = () => {
        if (!titleRef.current) return;
        const rect = titleRef.current.getBoundingClientRect();

        // GAP visual opcional entre o título e o menu
        const GAP = 8;

        // MUI v4 espera coordenadas RELATIVAS AO VIEWPORT (não somar scroll).
        setMenuPos({
            top: rect.bottom + GAP,
            left: rect.left
        });

        setIsMenuOpen(true);
    };


    const handleQueueChange = (queueId, queueName) => {
        setSelectedQueue(queueId);
        setCurrentQueueName(queueName);
        setAnchorEl(null);
        setIsMenuOpen(false);
    };



    return (
        <Container className={classes.root} maxWidth={false}>
            <Paper className={classes.paper}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Tooltip title={i18n.t("kanban.selectQueue") || "Clique para selecionar a fila"}>
                            <Box ref={titleRef} className={classes.titleContainer} onClick={handleTitleClick}>
                                <Typography variant="h5" component="h1" className={classes.titleText}>
                                    Kanban da Fila{" "}
                                    <Box
                                        component="span"
                                        className={classes.queueName}
                                        style={{ color: (queues.find(q => q.id.toString() === selectedQueue)?.color) || undefined }}
                                    >
                                        {currentQueueName}
                                    </Box>
                                </Typography>
                                <IconButton size="small">
                                    <ExpandMoreIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Menu
                            id="queue-menu"
                            keepMounted
                            open={isMenuOpen}
                            onClose={handleMenuClose}
                            // usar coordenadas em vez de anchorEl
                            anchorReference="anchorPosition"
                            anchorPosition={menuPos}
                            // alinhar o canto superior-esquerdo do menu ao ponto calculado
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                            // v4: evita reancoragem interna
                            getContentAnchorEl={null}
                            // opcional: igualar a largura ao título
                            PaperProps={{ style:  { width: 320 } }}
                        >
                            {queues.map(queue => (
                                <MenuItem
                                    key={queue.id}
                                    value={queue.id.toString()}
                                    onClick={() => handleQueueChange(queue.id.toString(), queue.name)}
                                    className={classes.queueMenuItem}
                                    selected={selectedQueue === queue.id.toString()}
                                >
                                    {queue.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>
                </Grid>
            </Paper>

            <div className={classes.boardContainer}>
                {selectedQueue && (
                    <KanbanBoard queueId={parseInt(selectedQueue)} />
                )}
            </div>

            <ToastContainer />
        </Container>
    );
};

export default Kanban;